import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

// Purchase Dashboard DTOs
export interface MonthlySummary {
  year: number;
  month: number;
  monthDate?: string; // Date string in format YYYY-MM-DD or similar
  monthName?: string;
  totalPurchaseValue: number;
  purchaseCount?: number;
  quantity?: number;
}

export interface CategoryDetail {
  categoryId: number;
  categoryName: string;
  totalPurchaseValue: number;
  purchaseCount?: number;
  quantity?: number;
  percentage?: number;
}

export interface SupplierDetail {
  supplierId: number;
  supplierName: string;
  totalPurchaseValue: number;
  purchaseCount?: number;
  quantity?: number;
  percentage?: number;
}

/**
 * Row-level monthly purchase summary including category and supplier.
 * Backend DTO: PurchaseDashboardDto.MonthlySummaryWithCategorySupplier
 *
 * Note: UI aggregates this client-side to build chart + tables and to support filters.
 */
export interface MonthlySummaryWithCategorySupplier {
  year: number;
  month: number;
  monthDate?: string;
  monthName?: string;

  categoryId?: number;
  categoryName?: string;
  supplierId?: number;
  supplierName?: string;

  totalPurchaseValue: number;
  purchaseCount?: number;
  quantity?: number;
  totalQuantity?: number;
}

// Purchase Dashboard API Service
class PurchaseDashboardApiService extends BaseApiService {
  
  // Get monthly purchase summary (optionally filtered by date range)
  async getMonthlySummary(
    branchId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<MonthlySummary[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<MonthlySummary[]>>(
        API_ENDPOINTS.PURCHASE_DASHBOARD.MONTHLY_SUMMARY(branchId, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('PurchaseDashboardApiService: Error fetching monthly summary:', error);
      toast.error('Failed to fetch monthly purchase summary');
      throw error;
    }
  }

  // Get monthly purchase summary with category and supplier (single endpoint for full dashboard)
  async getMonthlySummaryDetailed(
    branchId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<MonthlySummaryWithCategorySupplier[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<MonthlySummaryWithCategorySupplier[]>>(
        API_ENDPOINTS.PURCHASE_DASHBOARD.MONTHLY_SUMMARY_DETAILED(branchId, startDate, endDate),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch detailed monthly purchase summary');
      throw error;
    }
  }

  // Get purchase details by category for a specific month
  async getCategoryDetails(
    year: number,
    month: number,
    branchId?: number
  ): Promise<ApiResponse<CategoryDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<CategoryDetail[]>>(
        API_ENDPOINTS.PURCHASE_DASHBOARD.CATEGORY_DETAILS(year, month, branchId),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('PurchaseDashboardApiService: Error fetching category details:', error);
      toast.error('Failed to fetch category details');
      throw error;
    }
  }

  // Get purchase details by supplier for a specific month
  async getSupplierDetails(
    year: number,
    month: number,
    branchId?: number
  ): Promise<ApiResponse<SupplierDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<SupplierDetail[]>>(
        API_ENDPOINTS.PURCHASE_DASHBOARD.SUPPLIER_DETAILS(year, month, branchId),
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('PurchaseDashboardApiService: Error fetching supplier details:', error);
      toast.error('Failed to fetch supplier details');
      throw error;
    }
  }
}

export const purchaseDashboardApiService = new PurchaseDashboardApiService();

